import type { ReactNode } from 'react';
import { CodeSnippet } from '@backstage/core-components';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Text,
} from '@backstage/ui';

// ── Types ────────────────────────────────────────────────────────────────────

interface FieldOption {
  label: string;
  value: string;
}

interface CustomFieldEntry {
  /** The value used in `ui:field:` in template YAML */
  uiFieldName: string;
  /** Human-readable title */
  title: string;
  /** What the field does, shown in the card header */
  description: string;
  /** JSON Schema fragment the template parameter should carry */
  schemaFragment: string;
  /** Full usage snippet the template author can paste */
  usageSnippet: string;
  /** Optional enumerated options the field accepts */
  options?: FieldOption[];
  /** Optional extra ui:options documented for this field */
  uiOptions?: string;
}

// ── Registry data ─────────────────────────────────────────────────────────────
// Keep in sync with the extensions registered in packages/app/src/App.tsx and
// their implementations under packages/app/src/components/scaffolder/*/

const FIELDS: CustomFieldEntry[] = [
  {
    uiFieldName: 'EntityLinkTypePicker',
    title: 'Entity Link Type Picker',
    description:
      'A dropdown that lets template users categorise a link they are adding to an entity. ' +
      'Renders a <Select> pre-populated with the eight standard Make link types.',
    options: [
      { label: 'slack-channel', value: 'slack-channel' },
      { label: 'datadog', value: 'datadog' },
      { label: 'github', value: 'github' },
      { label: 'jira', value: 'jira' },
      { label: 'confluence', value: 'confluence' },
      { label: 'runbook', value: 'runbook' },
      { label: 'documentation', value: 'documentation' },
      { label: 'other', value: 'other' },
    ],
    schemaFragment: JSON.stringify(
      {
        title: 'Link Type',
        type: 'string',
        description: 'Type of the link',
      },
      null,
      2,
    ),
    usageSnippet: `parameters:
  - title: Add a link
    properties:
      linkType:
        title: Link Type
        type: string
        description: Type of the link
        ui:field: EntityLinkTypePicker`,
  },
  {
    uiFieldName: 'UniqueEntityName',
    title: 'Unique Entity Name',
    description:
      'A text field that validates the entered name is not already registered in the Backstage ' +
      'catalog before allowing the template to proceed. Uses async validation on blur.',
    schemaFragment: JSON.stringify(
      {
        title: 'Entity Name',
        type: 'string',
        description: 'Unique name of the entity',
      },
      null,
      2,
    ),
    usageSnippet: `parameters:
  - title: Choose a name
    properties:
      name:
        title: Entity Name
        type: string
        description: Unique name of the entity
        ui:field: UniqueEntityName`,
  },
  {
    uiFieldName: 'ProjectUpload',
    title: 'Project Upload',
    description:
      'A file-upload widget that accepts a local folder (via the browser File System Access API) ' +
      'or a .zip file. Respects .gitignore rules, strips lock files and build artefacts, then ' +
      'delivers the result as a base64-encoded zip string to the template step.',
    uiOptions: `ui:options:
  maxSize: 5242880        # max allowed bytes of the base64-encoded payload
  sizeLimitHint: >-       # message shown when the limit is exceeded
    Remove node_modules and dist before uploading.
  skipDefaultIgnores: false  # when true, dotfiles / lock files are kept
  skipGitignore: false       # when true, .gitignore rules are not applied`,
    schemaFragment: JSON.stringify(
      {
        title: 'Project Upload',
        type: 'string',
        description: 'Upload a folder or zip file containing your project',
      },
      null,
      2,
    ),
    usageSnippet: `parameters:
  - title: Upload your project
    properties:
      project:
        title: Project Upload
        type: string
        description: Upload a folder or zip file containing your project
        ui:field: ProjectUpload
        ui:options:
          maxSize: 5242880
          sizeLimitHint: Remove node_modules and dist before uploading.
          skipDefaultIgnores: false
          skipGitignore: false`,
  },
  {
    uiFieldName: 'PendingVibeUpdatePicker',
    title: 'Pending Vibe Update Picker',
    description:
      'A dropdown that lists the signed-in user’s open, promotable internal app pull requests ' +
      '(owner-scoped, fetched server-side from the cloudflare-workers backend) so they can pick which ' +
      'change to publish without opening GitHub. The selected value is the pull request URL, and the ' +
      'chosen change’s preview link is shown inline. Used by the “Publish an App Update” template.',
    schemaFragment: JSON.stringify(
      {
        title: 'Pending change to publish',
        type: 'string',
        description: 'Pick the pending change to publish',
      },
      null,
      2,
    ),
    usageSnippet: `parameters:
  - title: Approve & merge
    properties:
      pullRequestUrl:
        title: Pending change to publish
        type: string
        description: Pick the pending change to publish
        ui:field: PendingVibeUpdatePicker`,
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const MONO = { fontFamily: 'var(--bui-font-monospace)' } as const;

/** A monospace token in a neutral outline: ui:field names and option values. */
function CodeTag({ children }: { children: ReactNode }) {
  return (
    <Badge style={{ flexShrink: 0, border: '1px solid var(--bui-border-2)' }}>
      <Text
        variant="body-small"
        style={{
          ...MONO,
          // Trim the line box to the capitals so the text sits in the middle.
          textBox: 'trim-both cap alphabetic',
        }}
      >
        {children}
      </Text>
    </Badge>
  );
}

/** A small section label inside a field card. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text as="h3" variant="body-small" weight="bold" color="secondary">
      {children}
    </Text>
  );
}

function FieldCard({ field }: { field: CustomFieldEntry }) {
  return (
    <Card>
      <CardHeader>
        <Flex align="center" gap="2" style={{ flexWrap: 'wrap' }}>
          <Text as="h2" variant="title-x-small" weight="bold">
            {field.title}
          </Text>
          <CodeTag>{field.uiFieldName}</CodeTag>
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="3">
          <Text as="p">{field.description}</Text>

          {field.options && (
            <Flex direction="column" gap="1">
              <SectionLabel>Accepted values</SectionLabel>
              <Flex gap="1" style={{ flexWrap: 'wrap' }}>
                {field.options.map(opt => (
                  <CodeTag key={opt.value}>{opt.value}</CodeTag>
                ))}
              </Flex>
            </Flex>
          )}

          <Flex direction="column" gap="1">
            <SectionLabel>JSON Schema fragment</SectionLabel>
            <CodeSnippet
              text={field.schemaFragment}
              language="json"
              showCopyCodeButton
            />
          </Flex>

          {field.uiOptions && (
            <Flex direction="column" gap="1">
              <SectionLabel>Available ui:options</SectionLabel>
              <CodeSnippet
                text={field.uiOptions}
                language="yaml"
                showCopyCodeButton
              />
            </Flex>
          )}

          <Flex
            direction="column"
            gap="1"
            pt="3"
            style={{ borderTop: '1px solid var(--bui-border-1)' }}
          >
            <SectionLabel>
              Usage example (paste into parameters block)
            </SectionLabel>
            <CodeSnippet
              text={field.usageSnippet}
              language="yaml"
              showLineNumbers
              showCopyCodeButton
            />
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Lists the custom scaffolder field extensions registered in this Backstage
 * instance, with their JSON Schema fragments and copy-paste usage examples.
 *
 * Mounted at /create/custom-fields via CustomFieldsPage wrapper in blueprintRoutes.tsx.
 * No props — all data is statically defined in this file.
 */
export default function CustomFieldsRegistry(): JSX.Element {
  return (
    <Container py="6">
      <Flex direction="column" gap="2" mb="5">
        <Text as="h2" variant="title-small">
          Custom field extensions
        </Text>
        <Text as="p" variant="body-large" color="secondary">
          These custom fields extend the scaffolder form. Reference them in a
          template's <code style={MONO}>parameters</code> block using the{' '}
          <code style={MONO}>ui:field</code> key. The JSON Schema fragment must
          match the field's expected type.
        </Text>
      </Flex>

      <Flex direction="column" gap="5">
        {FIELDS.map(field => (
          <FieldCard key={field.uiFieldName} field={field} />
        ))}
      </Flex>
    </Container>
  );
}
